import { apiMutate } from "~/utils/apiClient";

// A pinned artifact spans three services. The Projects service stores the pin
// and serves it; the analysis-agent creates and refreshes one, because building
// or re-running a workflow needs artifact lineage the Projects service cannot
// see; the conversations service renders it, because it is the only one holding
// the artifact.
const { VITE_ANALYSIS_AGENT_API, VITE_CONVERSATIONS_API, VITE_PROJECTS_API } = import.meta.env;

export const projectPinnedArtifactsPath = (projectId: string) =>
  `${VITE_PROJECTS_API}/projects/${projectId}/pinned-artifacts`;

const projectPinnedArtifactPath = (projectId: string, pinId: string) =>
  `${projectPinnedArtifactsPath(projectId)}/${pinId}`;

/** Preview for a pinned artifact, authorized by the pin rather than by owning
 *  the conversation the artifact sits in — which is what lets every project
 *  member render a card someone else pinned. Resolving through the pin also
 *  means the card follows a refresh without asking for anything different. */
export const pinnedArtifactPreviewPath = (projectId: string, pinId: string) =>
  `${VITE_CONVERSATIONS_API}/projects/${projectId}/pinned-artifacts/${pinId}/preview`;

const pinArtifactPath = (projectId: string) =>
  `${VITE_ANALYSIS_AGENT_API}/projects/${projectId}/pinned-artifacts`;

/** How a step produces its artifact. A `frozen_input` cannot be re-run — a user
 *  upload or a file pulled from storage — and carries the reason why. */
export type StepKind = "sql" | "code" | "frozen_input";

export interface WorkflowStep {
  step_id: string;
  kind: StepKind;
  artifact_type: string;
  summary: string | null;
  code: string | null;
  runtime: string | null;
  result_variable: string | null;
  file_name: string | null;
  source_tables: string[];
  inputs: string[];
  frozen_reason: string | null;
}

/** The plan that reproduces an artifact, in dependency order: every step's
 *  `inputs` appear before it and the target is last. */
export interface WorkflowSpec {
  target_step_id: string;
  steps: WorkflowStep[];
}

// `current_*` is what the card renders now and moves to a newly produced
// artifact each time a refresh succeeds; `source_*` still records where the pin
// was made. They are equal until the first successful refresh.
export interface PinnedArtifact {
  id: string;
  project_id: string;
  source_conversation_id: string;
  source_artifact_name: string;
  current_conversation_id: string;
  current_artifact_name: string;
  display_name: string;
  recipe: WorkflowSpec;
  last_refresh_run_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface PinArtifactInput {
  conversation_id: string;
  artifact_name: string;
  display_name: string;
}

/** True when a pin's own workflow cannot be re-run, so Refresh should not be
 *  offered. The target is the last step, which the backend guarantees. */
export const isRefreshable = (pin: PinnedArtifact): boolean => {
  const target = pin.recipe.steps.find((s) => s.step_id === pin.recipe.target_step_id);
  return target !== undefined && target.kind !== "frozen_input";
};

/** Pin an artifact to a project's overview. Goes to the analysis-agent, which
 *  derives the workflow from the artifact's lineage before handing it to the
 *  Projects service to store. Conflicts (409) if already pinned. */
export async function pinArtifact(
  projectId: string,
  input: PinArtifactInput
): Promise<PinnedArtifact> {
  return apiMutate<PinnedArtifact>(pinArtifactPath(projectId), "POST", input);
}

/** Unpin an artifact. The artifact itself is untouched — it belongs to the
 *  session that produced it, and a pin was only ever a reference. */
export async function unpinArtifact(projectId: string, pinId: string): Promise<void> {
  await apiMutate<null>(projectPinnedArtifactPath(projectId, pinId), "DELETE", undefined, {
    expects204: true,
  });
}
